/**
 * Factory for WebSked pitches
 * @author Umer Hassan, Brendan Magee
 */

function PitchFactory(
  $http,
  $q,
  $log,
  $translate,
  pitchStatusIndex,
  analyticsService,
  toastWrapperService,
  platformService
) {
  class PitchError extends Error {
    constructor(message) {
      super(message);
      this.name = 'PitchError';
    }
  }

  return class Pitch {
    constructor(type, data) {
      if (typeof type !== 'string') {
        throw new PitchError('the Pitch constructor takes a `type` string as its first argument');
      }

      const pitchData = { type, ...data };

      if (data.currentStatus) {
        pitchData.currentStatusLabel = pitchStatusIndex.getStatusName(data.currentStatus);
      }

      if (data.events && data.events.length) {
        const latestEvent = data.events[data.events.length - 1];
        pitchData.currentNote = latestEvent.note;
        pitchData.latestEventTime = latestEvent.datetime;
        pitchData.latestUser = latestEvent.person;

        pitchData.events = data.events.map(event => ({
          ...event,
          statusLabel: pitchStatusIndex.getStatusName(event.pitchStatus)
        }));

        if (type === 'publication') {
          const { canHoldUntil } = latestEvent;
          pitchData.canHoldUntil = canHoldUntil ? canHoldUntil * 1000 : null;
          pitchData.currentEditionTime = latestEvent.editionTime;
        }
      }

      if (type === 'platform' && !pitchData.platformName) {
        pitchData.platformName = platformService.getPlatformName(data.planPath, data.website);
      } else if (type === 'publication') {
        pitchData.currentEditionId = data.currentEdition;
      }

      Object.assign(this, pitchData);
    }

    static createBasicPayload(data) {
      return {
        status: data.newStatus || 'pitched',
        note: data.newNote,
        storyId: data.storyId,
        breaking: data.breaking
      };
    }

    static createBulkUpdatePayload({ publicationId, sourceArray, destination }) {
      if (publicationId && sourceArray && Array.isArray(sourceArray) && destination) {
        const source = sourceArray.reduce((acc, pitch) => {
          const { currentEditionId, currentStatus } = pitch;
          const existing = acc.find(src =>
            (src.editionId === currentEditionId && src.pitchStatus === currentStatus));

          if (existing) {
            existing.storyIds.push(pitch.storyId);
          } else {
            acc.push({
              pitchStatus: pitch.currentStatus,
              editionId: pitch.currentEditionId,
              storyIds: [
                pitch.storyId
              ]
            });
          }

          return acc;
        }, []);

        return { publicationId, source, destination };
      }

      return null;
    }

    static throwIfPropFalsy(obj, propName) {
      if (!obj[propName]) {
        throw new PitchError(`Pitch instance missing property: ${propName}`);
      }
    }

    static createMultiple(type, data, destinations) {
      let payload = Pitch.createBasicPayload(data);
      payload = Object.assign(payload, data, { destinations });

      return $http.post(Pitch.endpoints[type], payload)
        .then((response) => {
          const result = response.data.map((value) => {
            const val = { ...value };
            val.pitch.storyId = data.storyId;
            return new Pitch(type, val.pitch);
          });

          analyticsService.trackEvent(`${type} pitch management`, 'Pitches created');
          return result;
        });
    }

    static bulkUpdate(requestData) {
      const payload = Pitch.createBulkUpdatePayload(requestData);

      if (!payload) {
        return $q.reject('The bulk update payload is missing a required property');
      }

      return $http.post(Pitch.endpoints.bulk, payload)
        .then((response) => {
          if (response.status === 200) {
            const count = requestData.sourceArray.length;
            const successMessage = $translate.instant('PITCH_UPDATE_BULK_SUCCESS', { count });

            toastWrapperService.createToast(successMessage, 'success');
            analyticsService.trackEvent('publication pitch management', 'Bulk pitch update');
            return true;
          }
          $log.warn(`Bulk update API response with status ${response.status}:`, response);

          return false;
        });
    }

    static get endpoints() {
      return {
        bulk: 'api/stories/pitches/publications/bulk',
        platform: 'api/stories/pitches/platforms',
        publication: 'api/stories/pitches/publications'
      };
    }

    get pitchStatuses() {
      return pitchStatusIndex.getListCopy(`${this.type}Statuses`);
    }

    get lastNote() {
      if (this.currentNote) {
        return ` // ${this.latestUser.name}: "${this.currentNote}"`;
      }
      return '';
    }

    get pubCardNote() {
      const events = this.events.filter(event => event.note);

      if (events.length) {
        const { note, person } = events[events.length - 1];
        return { note, author: person.name };
      }

      return { note: null, author: null };
    }

    save() {
      const endpoint = Pitch.endpoints[this.type];

      Pitch.throwIfPropFalsy(this, 'storyId');

      const payload = Pitch.createBasicPayload(this);

      if (this.type === 'platform') {
        ['planPath', 'website']
          .forEach(prop => Pitch.throwIfPropFalsy(this, prop));

        payload.destinations = [{
          platformPath: this.planPath,
          website: this.website
        }];
      } else if (this.type === 'publication') {
        ['publicationId', 'sectionId']
          .forEach(prop => Pitch.throwIfPropFalsy(this, prop));

        const destTime = this.newEditionTime || null;

        payload.publicationId = this.publicationId;
        payload.destinations = [{
          publicationId: this.publicationId,
          sectionId: this.sectionId,
          canHoldUntil: this.canHoldUntil ? Math.floor(this.canHoldUntil / 1000) : null,
          editionTime: destTime
        }];
      }

      return $http.post(endpoint, payload)
        .then((response) => {
          const [responseObject] = response.data;
          const result = new Pitch(this.type, responseObject.pitch);
          result.storyId = this.storyId;
          analyticsService.trackEvent(`${this.type} pitch management`, 'Pitch updated');
          return result;
        });
    }

    appendLastNote() {
      const existingNewNote = this.newNote ? this.newNote : '';
      let newString = `${existingNewNote} // ${this.latestUser.name}`;

      if (this.currentNote) {
        newString += ` '${this.currentNote}'`;
      }

      this.newNote = newString;
      analyticsService.trackEvent(`${this.type} pitch management`, 'Appended last note');
    }
  };
}

const arr = [
  '$http',
  '$q',
  '$log',
  '$translate',
  'pitchStatusIndex',
  'analyticsService',
  'toastWrapperService',
  'platformService',
  PitchFactory
];

export default function (ngModule) {
  ngModule.factory('Pitch', arr);
}
